import { getBlockContent, isAnswerOperation, type StreamingBlockId, type StreamingChatId } from "@storytell/streaming";
import { stripCitationsFromMarkdown } from "~/utils/copyRichText";
import type { useWire } from "~/wire";

/**
 * Extracts markdown content from an artifact block
 * @param blockId - The block ID
 * @param wire - The wire instance
 * @returns The markdown content as a string
 */
export const getArtifactMarkdown = (blockId: string, wire: ReturnType<typeof useWire>): string => {
  // Try chat store first (used by ArtifactViewer)
  const chatBlock = wire.chat.getBlock(blockId as StreamingBlockId);
  if (chatBlock) {
    // Use finalContent if available (completed blocks), otherwise get from granular store
    const rawMarkdown =
      chatBlock.finalContent?.type === "text" ? chatBlock.finalContent.text : (getBlockContent(blockId) ?? "");
    return stripCitationsFromMarkdown(rawMarkdown);
  }

  // Fallback to stream store (used by AnswerRenderer)
  const streamBlock = wire.stream.block.get(blockId as StreamingBlockId);
  if (streamBlock) {
    const rawMarkdown =
      streamBlock.finalContent?.type === "text" ? streamBlock.finalContent.text : (getBlockContent(blockId) ?? "");
    return stripCitationsFromMarkdown(rawMarkdown);
  }

  return "";
};

const SECTION_SEPARATOR = "\n\n---\n\n";

/**
 * Collects markdown from all answer blocks in a chat (for "download full response").
 */
export const getFullChatMarkdown = (chatId: StreamingChatId | undefined, wire: ReturnType<typeof useWire>): string => {
  if (!chatId) return "";
  const chat = wire.stream.chat.get(chatId);
  const messageIds = chat?.messages ?? [];
  const parts: string[] = [];
  for (const messageId of messageIds) {
    const message = wire.stream.message.get(messageId);
    if (!message?.operations) continue;
    for (const opId of message.operations) {
      const op = wire.stream.operation.get(opId);
      if (!op || !isAnswerOperation(op.activity) || !op.blocks) continue;
      for (const blockId of op.blocks) {
        const md = getArtifactMarkdown(blockId, wire);
        if (md.trim()) parts.push(md.trim());
      }
    }
  }
  return parts.join(SECTION_SEPARATOR);
};
